import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { GoogleMap, Marker, DirectionsRenderer } from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMapMarkerAlt, 
  faLocationCrosshairs, 
  faMotorcycle
} from "@fortawesome/free-solid-svg-icons";
import { useFirebaseCartById } from "../../hooks/useFirebaseCartData";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";
import { toast } from "react-toastify";
import "./index.css";

interface LiveLocation {
  _latitude: number;
  _longitude: number;
  langitude?: number;
  timestamp?: any;
}

interface DriverLocationModalProps {
  show: boolean;
  onHide: () => void;
  cartId: string | null;
  orderDetails?: any;
}

const containerStyle = {
  width: "100%",
  height: "400px",
};

const DriverLocationModal: React.FC<DriverLocationModalProps> = ({
  show,
  onHide,
  cartId,
  orderDetails,
}) => {
  const [driverLocation, setDriverLocation] = useState<LiveLocation | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ 
    lat: 6.9270786, 
    lng: 79.861243 
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLocationStale, setIsLocationStale] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [locationUpdateTime, setLocationUpdateTime] = useState<string>("");
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const testDeliveryMarkerRef = useRef<google.maps.Marker | null>(null);
  const testInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const testDriverMarkerRef = useRef<google.maps.Marker | null>(null);
  const testDriverInfoRef = useRef<google.maps.InfoWindow | null>(null);

  // Use the project's Google Maps hook for consistency
  const { isLoaded } = useGoogleMaps();

  // Cart query for live location
  const { data: cartData, loading: isCartLoading, error: cartError } = useFirebaseCartById(cartId!);
  // Dummy refetchCart for compatibility (no refetch in hook, so just a no-op)
  const refetchCart = async () => {};

  // Update driver location when cart data changes or modal opens
  useEffect(() => {
    if (cartData?.liveLocation && show) {
      let location = cartData.liveLocation;

      // Support both old (_latitude/_longitude) and new (latitude/longitude) formats
      const lat = location._latitude ?? location.latitude;
      const lng = location._longitude ?? location.longitude;

      if (lat != null && lng != null) {
        setDriverLocation({
          _latitude: lat,
          _longitude: lng,
          timestamp: location.timestamp,
        });

        // Update map center to driver location
        setMapCenter({ lat, lng });

        // Check if location is stale (older than 5 minutes)
        const locationTime = location.timestamp?._seconds
          ? new Date(location.timestamp._seconds * 1000)
          : new Date();

        const now = new Date();
        const timeDiff = now.getTime() - locationTime.getTime();
        const isStale = timeDiff > 5 * 60 * 1000; // 5 minutes

        setIsLocationStale(isStale);
        setLocationUpdateTime(locationTime.toLocaleTimeString());
      } else {
        setDriverLocation(null);
      }
    }
  }, [cartData, show]);

  // Initialize directions service when Google Maps is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      setDirectionsService(new window.google.maps.DirectionsService());
    }
  }, [isLoaded]);

  // Map onLoad handler - save map instance
  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  // Map onUnmount handler - clear ref
  const handleMapUnmount = () => {
    mapRef.current = null;
  };

  // Calculate directions when both driver location and delivery address are available
  useEffect(() => {
    if (directionsService && driverLocation && orderDetails?.deliveryDetails?.coordinates && show) {
      const deliveryLat = orderDetails.deliveryDetails.coordinates.latitude;
      const deliveryLng = orderDetails.deliveryDetails.coordinates.longitude;
      
      const request: google.maps.DirectionsRequest = {
        origin: { lat: driverLocation._latitude, lng: driverLocation._longitude },
        destination: { lat: deliveryLat, lng: deliveryLng },
        travelMode: google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, (result, status) => {
        if (status === 'OK' && result) {
          setDirections(result);
        } else {
          console.error("Directions request failed:", status);
          setDirections(null);
        }
      });
    }
  }, [directionsService, driverLocation, orderDetails?.deliveryDetails?.coordinates, show]);

  // Fit map bounds to include both driver and delivery markers (and route)
  useEffect(() => {
    if (!mapRef.current) return;

    const deliveryLat = orderDetails?.deliveryDetails?.coordinates?.latitude;
    const deliveryLng = orderDetails?.deliveryDetails?.coordinates?.longitude;

    if (driverLocation && deliveryLat && deliveryLng) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(new window.google.maps.LatLng(driverLocation._latitude, driverLocation._longitude));
      bounds.extend(new window.google.maps.LatLng(deliveryLat, deliveryLng));
      try {
        mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 } as any);
      } catch (err) {
        // Older google maps typings may not accept padding object; fallback
        try {
          mapRef.current.fitBounds(bounds);
        } catch (err2) {
          console.warn('fitBounds failed', err2);
        }
      }
    } else if (driverLocation && mapRef.current) {
      // If only driver location exists, center on it
      mapRef.current.setCenter({ lat: driverLocation._latitude, lng: driverLocation._longitude });
      mapRef.current.setZoom(15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, driverLocation, orderDetails?.deliveryDetails?.coordinates, directions, show]);

  // When directions are ready, ensure delivery marker is visible by panning slightly
  useEffect(() => {
    if (!mapRef.current || !directions) return;
    const deliveryLat = orderDetails?.deliveryDetails?.coordinates?.latitude;
    const deliveryLng = orderDetails?.deliveryDetails?.coordinates?.longitude;
    if (deliveryLat && deliveryLng) {
      try {
        // pan a bit towards driver to ensure marker isn't overlapped by polyline
        mapRef.current.panTo({ lat: deliveryLat + 0.00005, lng: deliveryLng + 0.00005 });
      } catch (err) {
        console.warn('PanTo failed', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directions]);

  // Add a native (non-React) Google Maps marker + InfoWindow for delivery for debug/visibility
  useEffect(() => {
    const deliveryLat = orderDetails?.deliveryDetails?.coordinates?.latitude;
    const deliveryLng = orderDetails?.deliveryDetails?.coordinates?.longitude;
    if (!mapRef.current || !deliveryLat || !deliveryLng) return;

    // Clean up any previous test marker/infoWindow
    if (testDeliveryMarkerRef.current) {
      testDeliveryMarkerRef.current.setMap(null);
      testDeliveryMarkerRef.current = null;
    }
    if (testInfoWindowRef.current) {
      testInfoWindowRef.current.close();
      testInfoWindowRef.current = null;
    }

    try {
      const marker = new window.google.maps.Marker({
        position: { lat: deliveryLat, lng: deliveryLng },
        map: mapRef.current,
        title: 'Delivery (native marker)',
        zIndex: 99999,
      });
      testDeliveryMarkerRef.current = marker;

      const info = new window.google.maps.InfoWindow({
        content: `<div style="font-weight:bold">Delivery</div><div>${orderDetails?.deliveryDetails?.address || ''}</div>`
      });
      testInfoWindowRef.current = info;

      // Open info window briefly to draw attention
      info.open({ anchor: marker, map: mapRef.current });
      setTimeout(() => info.close(), 3000);
    } catch (err) {
      console.warn('Failed to add native marker', err);
    }

    return () => {
      if (testDeliveryMarkerRef.current) {
        testDeliveryMarkerRef.current.setMap(null);
        testDeliveryMarkerRef.current = null;
      }
      if (testInfoWindowRef.current) {
        testInfoWindowRef.current.close();
        testInfoWindowRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, orderDetails?.deliveryDetails?.coordinates, show]);

  // Add a native (non-React) Google Maps marker + InfoWindow for driver to ensure visibility
  useEffect(() => {
    if (!mapRef.current || !driverLocation || !show) return;

    // Clean up any previous test driver marker/infoWindow
    if (testDriverMarkerRef.current) {
      testDriverMarkerRef.current.setMap(null);
      testDriverMarkerRef.current = null;
    }
    if (testDriverInfoRef.current) {
      testDriverInfoRef.current.close();
      testDriverInfoRef.current = null;
    }

    try {
      const marker = new window.google.maps.Marker({
        position: { lat: driverLocation._latitude, lng: driverLocation._longitude },
        map: mapRef.current,
        title: 'Driver (native marker)',
        zIndex: 99998,
      });
      testDriverMarkerRef.current = marker;

      const info = new window.google.maps.InfoWindow({
        content: `<div style="font-weight:bold">Driver</div><div>${cartData?.driverName || cartData?.name || cartData?.displayName || 'Driver'}</div>`
      });
      testDriverInfoRef.current = info;
      // Open briefly
      info.open({ anchor: marker, map: mapRef.current });
      setTimeout(() => info.close(), 2500);
    } catch (err) {
      console.warn('Failed to add native driver marker', err);
    }

    return () => {
      if (testDriverMarkerRef.current) {
        testDriverMarkerRef.current.setMap(null);
        testDriverMarkerRef.current = null;
      }
      if (testDriverInfoRef.current) {
        testDriverInfoRef.current.close();
        testDriverInfoRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, driverLocation, show, cartData]);

  // Ensure map is properly initialized when modal opens
  useEffect(() => {
    if (show && driverLocation && isLoaded) {
      const newCenter = {
        lat: driverLocation._latitude,
        lng: driverLocation._longitude
      };
      setMapCenter(newCenter);
    }
  }, [show, driverLocation, isLoaded]);

  const handleRefreshLocation = async () => {
    try {
      await refetchCart();
      toast.success("Driver location refreshed", {
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Failed to refresh driver location:", error);
      toast.error("Failed to refresh location");
    }
  };

  const openInGoogleMaps = () => {
    if (driverLocation) {
      const url = `https://www.google.com/maps?q=${driverLocation._latitude},${driverLocation._longitude}`;
      window.open(url, '_blank');
    }
  };

  if (!cartId) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#3f0213', color: 'white' }}>
        <Modal.Title>
          <FontAwesomeIcon icon={faMotorcycle} className="me-2" />
          Live Driver Location
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Loading State */}
        {isCartLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" className="me-2" />
            <span>Loading driver location...</span>
          </div>
        )}

        {/* Error State */}
        {cartError && (
          <Alert variant="warning" className="mb-3">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
            Unable to load driver location. Please try refreshing.
          </Alert>
        )}

        {/* No Location Data */}
        {!isCartLoading && !cartError && !driverLocation && (
          <Alert variant="info" className="mb-3">
            <FontAwesomeIcon icon={faLocationCrosshairs} className="me-2" />
            Driver location not available yet. Location will appear when the driver starts moving.
          </Alert>
        )}

        {/* Google Map */}
        <div className="map-container mb-3">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={15}
              onLoad={handleMapLoad}
              onUnmount={handleMapUnmount}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                fullscreenControl: true,
                mapTypeControl: true
              }}
            >
              {/* Driver Location Marker */}
              {driverLocation && show ? (
                isLoaded && (window as any).google ? (
                  <Marker
                    key={`driver-motorcycle-${driverLocation._latitude}-${driverLocation._longitude}-${show}`}
                    position={{
                      lat: driverLocation._latitude,
                      lng: driverLocation._longitude
                    }}
                    icon={{
                      url: 'data:image/svg+xml;base64,' + btoa(`
                        <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                          <!-- Motorcycle wheels -->
                          <circle cx="7" cy="24" r="5" fill="none" stroke="#3f0213" stroke-width="2"/>
                          <circle cx="25" cy="24" r="5" fill="none" stroke="#3f0213" stroke-width="2"/>
                          <!-- Motorcycle body/frame -->
                          <path d="M12 24 L16 16 L20 16 L22 20 L25 24" fill="none" stroke="#3f0213" stroke-width="2"/>
                          <!-- Motorcycle seat -->
                          <path d="M14 16 L18 16" stroke="#3f0213" stroke-width="3" stroke-linecap="round"/>
                          <!-- Motorcycle engine -->
                          <rect x="16" y="18" width="4" height="4" fill="#3f0213" rx="1"/>
                          <!-- Headlight -->
                          <circle cx="23" cy="18" r="2" fill="#FFD700" stroke="#3f0213" stroke-width="1"/>
                          <!-- Rider -->
                          <circle cx="15" cy="12" r="2.5" fill="#3f0213"/>
                          <path d="M15 14 L15 16" stroke="#3f0213" stroke-width="2"/>
                        </svg>
                      `),
                      scaledSize: new window.google.maps.Size(32, 32),
                      anchor: new window.google.maps.Point(16, 29),
                    }}
                    title={`Driver: ${cartData?.driverName || cartData?.name || cartData?.displayName || "Driver"} - Live Location`}
                    zIndex={10000}
                  />
                ) : (
                  <Marker
                    key={`driver-fallback-${driverLocation._latitude}-${driverLocation._longitude}-${show}`}
                    position={{ lat: driverLocation._latitude, lng: driverLocation._longitude }}
                    title={`Driver: ${cartData?.driverName || cartData?.name || cartData?.displayName || "Driver"} - Live Location`}
                    zIndex={10000}
                  />
                )
              ) : null}

              {/* Delivery Address Marker (if available) */}
              {(() => {
                // Try multiple possible property names for delivery location
                const deliveryLat = orderDetails?.deliveryDetails?.coordinates?.latitude ||
                                  orderDetails?.deliveryLocation?.latitude || 
                                  orderDetails?.deliveryAddress?.latitude ||
                                  orderDetails?.customerAddress?.latitude ||
                                  orderDetails?.address?.latitude ||
                                  orderDetails?.location?.latitude ||
                                  orderDetails?.deliveryLatitude;

                const deliveryLng = orderDetails?.deliveryDetails?.coordinates?.longitude ||
                                  orderDetails?.deliveryLocation?.longitude || 
                                  orderDetails?.deliveryAddress?.longitude ||
                                  orderDetails?.customerAddress?.longitude ||
                                  orderDetails?.address?.longitude ||
                                  orderDetails?.location?.longitude ||
                                  orderDetails?.deliveryLongitude;

                return show && deliveryLat && deliveryLng && isLoaded && window.google ? (
                  <Marker
                    key={`delivery-${deliveryLat}-${deliveryLng}-${show}`}
                    position={{
                      lat: deliveryLat,
                      lng: deliveryLng
                    }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 15,
                      fillColor: "#FF0000",
                      fillOpacity: 1,
                      strokeWeight: 3,
                      strokeColor: "#FFFFFF"
                    }}
                    title={`Delivery Address: ${orderDetails?.deliveryDetails?.address || orderDetails?.address || 'Delivery Location'}`}
                    zIndex={9999}
                  />
                ) : (
                  // Fallback: Simple default marker if Google Maps symbols aren't ready
                  show && deliveryLat && deliveryLng ? (
                    <Marker
                      key={`delivery-fallback-${deliveryLat}-${deliveryLng}-${show}`}
                      position={{
                        lat: deliveryLat,
                        lng: deliveryLng
                      }}
                      title={`Delivery Address: ${orderDetails?.deliveryDetails?.address || orderDetails?.address || 'Delivery Location'}`}
                      zIndex={9999}
                    />
                  ) : null
                );
              })()}

              {/* Directions Renderer */}
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true, // We use custom markers
                    polylineOptions: {
                      strokeColor: "#3f0213",
                      strokeOpacity: 0.8,
                      strokeWeight: 4,
                      zIndex: 1,
                    },
                  }}
                />
              )}
            </GoogleMap>
          ) : (
            <div className="map-loading">
              <div className="text-center py-5">
                <Spinner animation="border" className="mb-2" />
                <div>Loading map...</div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {driverLocation && (
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={openInGoogleMaps}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                Open in Google Maps
              </Button>
            )}
          </div>
          <div>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleRefreshLocation}
              disabled={isCartLoading}
              className="me-2"
            >
              <FontAwesomeIcon icon={faLocationCrosshairs} className="me-1" />
              Refresh
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DriverLocationModal;
